import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { NAV } from "@/constants/testIds";
import { SERVICE_CATEGORIES, INDUSTRIES, BRAND } from "@/lib/data";
import { useTheme } from "@/hooks/useTheme";
import GoldButton from "./GoldButton";
import ThemeSwitch from "./ThemeSwitch";
import LanguageSwitcher from "./LanguageSwitcher";

const primaryLinksSchema = [
  { to: "/work", tKey: "nav.work", key: "work" },
  { to: "/case-studies", tKey: "nav.cases", key: "cases" },
  { to: "/services", tKey: "nav.services", key: "services" },
  { to: "/industries", tKey: "nav.industries", key: "industries" },
  { to: "/about", tKey: "nav.about", key: "about" },
  { to: "/founder", tKey: "nav.founder", key: "founder" },
  { to: "/insights", tKey: "nav.insights", key: "insights" },
  { to: "/blog", tKey: "nav.blog", key: "blog" },
  { to: "/questions", tKey: "nav.qa", key: "qa" },
  { to: "/contact", tKey: "nav.contact", key: "contact" },
];

const overlayEase = [0.76, 0, 0.24, 1];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { theme } = useTheme();
  const { t } = useTranslation();
  const primaryLinks = primaryLinksSchema.map((l) => ({ ...l, label: t(l.tKey) }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Close on route change (Escape key)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <header
        data-testid={NAV.root}
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          scrolled || open
            ? "bg-ink-950/80 backdrop-blur-xl border-b border-ivory/[0.06]"
            : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between px-6 lg:h-20 lg:px-10">
          <Link
            to="/"
            data-testid={NAV.logo}
            className="flex items-center gap-3"
            aria-label="Ragi home"
          >
            <img
              src={theme === "light" ? BRAND.logo.dark : BRAND.logo.light}
              alt="Ragi"
              className="h-9 w-auto object-contain lg:h-10"
              width="1200"
              height="296"
            />
          </Link>

          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageSwitcher />
            <ThemeSwitch />
            <MenuButton open={open} onClick={() => setOpen((v) => !v)} />
          </div>
        </div>
      </header>

      <MenuOverlay open={open} onClose={() => setOpen(false)} primaryLinks={primaryLinks} t={t} />
    </>
  );
}

function MenuButton({ open, onClick }) {
  return (
    <button
      data-testid={NAV.menuToggle}
      onClick={onClick}
      aria-label={open ? "Close menu" : "Open menu"}
      aria-expanded={open}
      className="group relative flex h-11 w-11 items-center justify-center rounded-full border border-ivory/10 text-ivory transition-colors hover:border-gold hover:text-gold"
    >
      <div className="relative flex h-4 w-5 flex-col justify-between">
        <motion.span
          animate={open ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
          transition={{ duration: 0.35, ease: overlayEase }}
          className="block h-px w-full origin-center bg-current"
        />
        <motion.span
          animate={open ? { opacity: 0, x: -8 } : { opacity: 1, x: 0 }}
          transition={{ duration: 0.25, ease: overlayEase }}
          className="block h-px w-full bg-current"
        />
        <motion.span
          animate={open ? { rotate: -45, y: -7 } : { rotate: 0, y: 0 }}
          transition={{ duration: 0.35, ease: overlayEase }}
          className="block h-px w-full origin-center bg-current"
        />
      </div>
    </button>
  );
}

function MenuOverlay({ open, onClose, primaryLinks, t }) {
  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key="menu"
          data-testid={NAV.megaMenu}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: overlayEase }}
          className="fixed inset-0 top-0 z-40 overflow-y-auto bg-ink-950/98 backdrop-blur-xl pt-16 lg:pt-20"
        >
          {/* Ambient gold glow */}
          <div
            className="pointer-events-none absolute inset-0"
            aria-hidden
            style={{
              background:
                "radial-gradient(600px circle at 20% 30%, rgba(212,175,55,0.08), transparent 60%)",
            }}
          />

          <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 gap-y-14 px-6 py-14 lg:grid-cols-12 lg:gap-x-16 lg:px-10 lg:py-20">
            {/* Column 1: primary */}
            <nav className="lg:col-span-5">
              <div className="flex items-center gap-3 font-sans text-[11px] uppercase tracking-widest text-gold">
                <span className="h-px w-8 bg-gold" />
                Navigate
              </div>
              <ul className="mt-8 space-y-1">
                {primaryLinks.map((l, i) => (
                  <OverlayLink
                    key={l.key}
                    to={l.to}
                    label={l.label}
                    index={i}
                    onClose={onClose}
                    testId={
                      NAV[
                        l.key === "cases"
                          ? "workLink"
                          : l.key === "founder"
                          ? "aboutLink"
                          : `${l.key}Link`
                      ] || undefined
                    }
                  />
                ))}
              </ul>
            </nav>

            {/* Column 2: services */}
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3 font-sans text-[11px] uppercase tracking-widest text-gold">
                <span className="h-px w-8 bg-gold" />
                {t("nav.services")}
              </div>
              <div className="mt-8 grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-2">
                {SERVICE_CATEGORIES.map((cat, ci) => (
                  <motion.div
                    key={cat.slug}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.5,
                      delay: 0.15 + ci * 0.05,
                      ease: overlayEase,
                    }}
                  >
                    <div className="font-sans text-[10px] uppercase tracking-widest text-ivory-muted">
                      {cat.label}
                    </div>
                    <ul className="mt-3 space-y-1.5">
                      {cat.services.slice(0, 4).map((s) => (
                        <li key={s.slug}>
                          <Link
                            to={`/services/${s.slug}`}
                            onClick={onClose}
                            className="font-display text-base leading-snug tracking-tight text-ivory transition-colors hover:text-gold"
                          >
                            {s.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
              <Link
                to="/services"
                onClick={onClose}
                className="mt-8 inline-flex items-center gap-3 font-sans text-[11px] uppercase tracking-widest text-gold hover:text-ivory"
              >
                {t("nav.allServices")}
                <span className="h-px w-8 bg-current" />
              </Link>
            </div>

            {/* Column 3: industries + contact */}
            <aside className="lg:col-span-3">
              <div className="flex items-center gap-3 font-sans text-[11px] uppercase tracking-widest text-gold">
                <span className="h-px w-8 bg-gold" />
                {t("nav.industries")}
              </div>
              <ul className="mt-8 grid grid-cols-2 gap-x-4 gap-y-2 lg:grid-cols-1">
                {INDUSTRIES.slice(0, 8).map((ind) => (
                  <li key={ind.slug}>
                    <Link
                      to={`/industries/${ind.slug}`}
                      onClick={onClose}
                      className="font-display text-base leading-snug tracking-tight text-ivory transition-colors hover:text-gold"
                    >
                      {ind.name}
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                to="/industries"
                onClick={onClose}
                className="mt-6 inline-flex items-center gap-3 font-sans text-[11px] uppercase tracking-widest text-gold hover:text-ivory"
              >
                {t("nav.allIndustries")}
                <span className="h-px w-8 bg-current" />
              </Link>

              <div className="mt-10 border-t border-ivory/[0.06] pt-6">
                <div className="mb-4 flex items-center gap-3 font-sans text-[11px] uppercase tracking-widest text-gold">
                  <span className="h-px w-8 bg-gold" />
                  Locations
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {["bengaluru","mumbai","delhi","dubai","singapore","london","new-york"].map((slug) => {
                    const name = { bengaluru: "Bengaluru", mumbai: "Mumbai", delhi: "Delhi", dubai: "Dubai", singapore: "Singapore", london: "London", "new-york": "New York" }[slug];
                    return (
                      <Link
                        key={slug}
                        to={`/locations/${slug}`}
                        onClick={onClose}
                        className="font-display text-base leading-snug tracking-tight text-ivory transition-colors hover:text-gold"
                      >
                        {name}
                      </Link>
                    );
                  })}
                </div>
                <Link
                  to="/locations"
                  onClick={onClose}
                  className="mt-4 inline-flex items-center gap-3 font-sans text-[11px] uppercase tracking-widest text-gold hover:text-ivory"
                >
                  All locations <span className="h-px w-8 bg-current" />
                </Link>
              </div>

              <div className="mt-12 border-t border-ivory/[0.06] pt-8">
                <div className="font-sans text-[10px] uppercase tracking-widest text-ivory-muted">
                  {t("nav.studioBengaluru")}
                </div>
                <div className="mt-4 space-y-2 font-sans text-sm">
                  <a
                    href={BRAND.phoneHref}
                    className="block text-ivory hover:text-gold transition-colors"
                    onClick={onClose}
                  >
                    {t("nav.call")} · {BRAND.phone}
                  </a>
                  <a
                    href={`mailto:${BRAND.email}`}
                    className="block text-ivory hover:text-gold transition-colors"
                    onClick={onClose}
                  >
                    {BRAND.email}
                  </a>
                  <a
                    href={BRAND.whatsappHref}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="block text-ivory hover:text-gold transition-colors"
                  >
                    {t("nav.whatsapp")} →
                  </a>
                </div>
              </div>
            </aside>
          </div>

          {/* Footer strip inside overlay */}
          <div className="relative border-t border-ivory/[0.06]">
            <div className="mx-auto flex max-w-[1440px] flex-col items-start justify-between gap-4 px-6 py-6 font-sans text-[11px] uppercase tracking-widest text-ivory-dim md:flex-row md:items-center lg:px-10">
              <span>© {new Date().getFullYear()} Ragi Media · Bengaluru</span>
              <span className="text-gold">{t("nav.signature")}</span>
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

function OverlayLink({ to, label, index, onClose, testId }) {
  return (
    <li className="overflow-hidden border-b border-ivory/[0.06]">
      <motion.div
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        exit={{ y: "110%" }}
        transition={{
          duration: 0.7,
          delay: 0.1 + index * 0.05,
          ease: overlayEase,
        }}
      >
        <Link
          to={to}
          onClick={onClose}
          data-testid={testId}
          className="group flex items-center justify-between py-3"
        >
          <span className="font-display text-4xl leading-none tracking-tighter text-ivory transition-colors group-hover:text-gold lg:text-6xl">
            {label}
          </span>
          <span className="font-display text-lg italic text-gold opacity-40 transition-opacity group-hover:opacity-100">
            0{index + 1}
          </span>
        </Link>
      </motion.div>
    </li>
  );
}
